import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { clientsAPI, cmsAPI } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Globe, Plus, PencilSimple, Trash, FileText, Wrench, Eye
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function WebsitePage() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [pages, setPages] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPage, setEditPage] = useState(null);
  const [form, setForm] = useState({
    title: '', slug: '', content: '', meta_title: '', meta_description: '', status: 'draft'
  });

  useEffect(() => {
    clientsAPI.list().then(r => {
      setClients(r.data);
      if (r.data.length > 0) setSelectedClient(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedClient) return;
    setLoading(true);
    Promise.all([
      cmsAPI.pages(selectedClient),
      cmsAPI.maintenance(selectedClient),
    ]).then(([p, m]) => {
      setPages(p.data);
      setMaintenance(m.data);
    }).catch(() => toast.error('Failed to load CMS data'))
      .finally(() => setLoading(false));
  }, [selectedClient]);

  const handleSave = async () => {
    try {
      if (editPage) {
        await cmsAPI.updatePage(editPage.id, form);
        toast.success('Page updated');
      } else {
        await cmsAPI.createPage({ ...form, client_id: selectedClient });
        toast.success('Page created');
      }
      setDialogOpen(false);
      setEditPage(null);
      setForm({ title: '', slug: '', content: '', meta_title: '', meta_description: '', status: 'draft' });
      const res = await cmsAPI.pages(selectedClient);
      setPages(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this page?')) return;
    try {
      await cmsAPI.deletePage(id);
      toast.success('Page deleted');
      const res = await cmsAPI.pages(selectedClient);
      setPages(res.data);
    } catch { toast.error('Failed to delete'); }
  };

  const openEdit = (p) => {
    setEditPage(p);
    setForm({
      title: p.title, slug: p.slug, content: p.content,
      meta_title: p.meta_title, meta_description: p.meta_description, status: p.status
    });
    setDialogOpen(true);
  };

  const categoryIcons = { security: '🔒', update: '🔄', performance: '⚡', backup: '💾' };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="website-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[var(--text-primary)] tracking-tight">
              Website CMS
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Content management, page builder & technical maintenance</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger data-testid="cms-client-select" className="w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]">
                <SelectValue placeholder="Select client" />
              </SelectTrigger>
              <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)]">{c.display_name || c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Dialog open={dialogOpen} onOpenChange={(v) => {
              setDialogOpen(v);
              if (!v) { setEditPage(null); setForm({ title: '', slug: '', content: '', meta_title: '', meta_description: '', status: 'draft' }); }
            }}>
              <DialogTrigger asChild>
                <Button data-testid="create-page-btn" className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
                  <Plus size={16} className="mr-2" /> New Page
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)] max-w-2xl" data-testid="page-dialog">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Chivo' }}>{editPage ? 'Edit Page' : 'New Page'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Title</label>
                      <Input data-testid="page-title-input" value={form.title}
                        onChange={e => setForm({...form, title: e.target.value})}
                        placeholder="Page title" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" />
                    </div>
                    <div>
                      <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Slug</label>
                      <Input data-testid="page-slug-input" value={form.slug}
                        onChange={e => setForm({...form, slug: e.target.value})}
                        placeholder="/about-us" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Content</label>
                    <textarea data-testid="page-content-input" value={form.content}
                      onChange={e => setForm({...form, content: e.target.value})}
                      placeholder="Write your page content..."
                      rows={8}
                      className="w-full bg-[var(--surface-sunken)] border border-[var(--border)] text-[var(--text-primary)] rounded-md px-3 py-2 text-sm placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)] focus:outline-none resize-none"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Meta Title</label>
                      <Input data-testid="page-meta-title-input" value={form.meta_title}
                        onChange={e => setForm({...form, meta_title: e.target.value})}
                        placeholder="SEO title" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" />
                    </div>
                    <div>
                      <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Status</label>
                      <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                        <SelectTrigger data-testid="page-status-select" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                          {['draft', 'published', 'archived'].map(s => (
                            <SelectItem key={s} value={s} className="text-[var(--text-primary)] hover:bg-[var(--neutral-bg)] capitalize">{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Meta Description</label>
                    <Input data-testid="page-meta-desc-input" value={form.meta_description}
                      onChange={e => setForm({...form, meta_description: e.target.value})}
                      placeholder="SEO description" className="bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]" />
                  </div>
                  <Button data-testid="save-page-btn" onClick={handleSave} className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
                    {editPage ? 'Update Page' : 'Create Page'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-6 h-6 border-2 border-[var(--accent-border)] border-t-[var(--accent)] rounded-full animate-spin" />
          </div>
        ) : (
          <Tabs defaultValue="pages" className="space-y-4">
            <TabsList className="bg-[var(--surface)] border border-[var(--border)]">
              <TabsTrigger value="pages" data-testid="tab-pages" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">
                <FileText size={14} className="mr-1.5" /> Pages ({pages.length})
              </TabsTrigger>
              <TabsTrigger value="maintenance" data-testid="tab-maintenance" className="data-[state=active]:bg-[var(--accent)] data-[state=active]:text-white">
                <Wrench size={14} className="mr-1.5" /> Maintenance
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pages" className="space-y-4">
              {pages.length === 0 ? (
                <div className="nx-metric-card rounded-lg text-center py-12">
                  <FileText size={40} className="mx-auto mb-3 text-[var(--text-tertiary)]" />
                  <p className="text-[var(--text-secondary)]">No pages yet. Create your first page!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {pages.map((p, i) => (
                    <div key={p.id} className="nx-metric-card rounded-lg animate-fade-in-up"
                      style={{ animationDelay: `${i * 50}ms` }} data-testid={`cms-page-${p.id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-display text-[var(--text-primary)] font-bold text-sm">{p.title}</h3>
                          <p className="text-xs text-[var(--text-tertiary)] font-mono mt-0.5">{p.slug}</p>
                        </div>
                        <span className={`nx-tag ${p.status === 'published' ? 'nx-tag-green' : p.status === 'draft' ? 'nx-tag-yellow' : 'nx-tag-gray'}`}>
                          {p.status}
                        </span>
                      </div>
                      {p.content && (
                        <p className="text-xs text-[var(--text-secondary)] line-clamp-2 mb-3">{p.content.substring(0, 120)}...</p>
                      )}
                      {p.meta_title && (
                        <p className="text-xs text-[var(--text-tertiary)] mb-3 truncate">
                          <span className="font-mono text-[var(--accent)]">SEO:</span> {p.meta_title}
                        </p>
                      )}
                      <div className="flex items-center gap-2 pt-3 border-t border-[var(--border-light)]">
                        <span className="text-xs text-[var(--text-tertiary)] font-mono">
                          {new Date(p.updated_at || p.created_at).toLocaleDateString()}
                        </span>
                        <div className="flex-1" />
                        <button data-testid={`edit-page-${p.id}`} onClick={() => openEdit(p)}
                          className="p-1.5 rounded hover:bg-[var(--neutral-bg)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors duration-200">
                          <PencilSimple size={16} />
                        </button>
                        <button data-testid={`delete-page-${p.id}`} onClick={() => handleDelete(p.id)}
                          className="p-1.5 rounded hover:bg-[var(--error-bg)] text-[var(--text-secondary)] hover:text-[var(--error)] transition-colors duration-200">
                          <Trash size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="maintenance" className="space-y-4">
              <div className="nx-metric-card rounded-lg" data-testid="maintenance-table">
                <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-4">
                  <Wrench size={18} className="inline mr-2" weight="duotone" />
                  Maintenance Log
                </h3>
                {maintenance.length === 0 ? (
                  <p className="text-[var(--text-secondary)] text-center py-8">No maintenance logs yet</p>
                ) : (
                  <div className="space-y-3">
                    {maintenance.map((m, i) => (
                      <div key={m.id} className="flex items-center gap-4 p-3 rounded border border-[var(--border-light)] animate-fade-in-up"
                        style={{ animationDelay: `${i * 50}ms` }}>
                        <span className="text-lg">{categoryIcons[m.category] || '📋'}</span>
                        <div className="flex-1">
                          <p className="text-sm text-[var(--text-primary)]">{m.task}</p>
                          <p className="text-xs text-[var(--text-tertiary)] font-mono mt-0.5">
                            {new Date(m.created_at).toLocaleDateString()} &middot; {m.category}
                          </p>
                        </div>
                        <span className="nx-tag nx-tag-green">{m.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </DashboardLayout>
  );
}
